import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Tabs,
    Tab,
    TextField,
    useTheme,
    Stack,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Checkbox,
    Tooltip,
    Card,
    CardContent,
    Grid,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import { purposeService, PurposePreferenceMapping, PurposeDataElementMapping } from '../../services/purpose-service';
import { dpOptionsService, DPOption } from '../../services/dpoptions-service';
import { tppService, ThirdPartyProcessor } from '../../services/tpp-service';
import { dataElementsService, DataElement } from '../../services/dataelements-service';
import { languageService, Language } from '../../services/language-service';
import { translationService } from '../../services/translation-service';
import AIIcon from '../../assets/images/Artificial_intelligence.png';

interface EditPurposePanelProps {
    open: boolean;
    onClose: () => void;
    purpose: any | null;
    onSave?: () => void;
}

interface InitialValues {
    name: string;
    description: string;
}

const EditPurposePanel: React.FC<EditPurposePanelProps> = ({ open, onClose, purpose, onSave }) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [initialValues, setInitialValues] = useState<InitialValues | undefined>();
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [activeLanguage, setActiveLanguage] = useState<string>('en');
    const [translations, setTranslations] = useState<Record<string, { name: string; description: string }>>({});
    const [originalTranslations, setOriginalTranslations] = useState<Record<string, { name: string; description: string }>>({});
    const [acceptedTranslations, setAcceptedTranslations] = useState<Set<string>>(new Set());
    const [isTranslating, setIsTranslating] = useState(false);
    const [showStartTranslation, setShowStartTranslation] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    // Preferences state
    const [mappedPreferences, setMappedPreferences] = useState<PurposePreferenceMapping[]>([]);
    const [availablePreferences, setAvailablePreferences] = useState<DPOption[]>([]);
    const [loadingPreferences, setLoadingPreferences] = useState(false);
    const [preferencesSearchTerm, setPreferencesSearchTerm] = useState('');
    const [addingPreferenceId, setAddingPreferenceId] = useState<number | null>(null);
    const [removingPreferenceId, setRemovingPreferenceId] = useState<number | null>(null);
    const [preferencesChanged, setPreferencesChanged] = useState(false);

    // Processors state
    const [mappedProcessors, setMappedProcessors] = useState<any[]>([]);
    const [availableProcessors, setAvailableProcessors] = useState<ThirdPartyProcessor[]>([]);
    const [loadingProcessors, setLoadingProcessors] = useState(false);
    const [processorsSearchTerm, setProcessorsSearchTerm] = useState('');
    const [addingProcessorId, setAddingProcessorId] = useState<number | null>(null);
    const [removingProcessorId, setRemovingProcessorId] = useState<number | null>(null);
    const [processorsChanged, setProcessorsChanged] = useState(false);

    // Data Elements state
    const [mappedDataElements, setMappedDataElements] = useState<PurposeDataElementMapping[]>([]);
    const [availableDataElements, setAvailableDataElements] = useState<DataElement[]>([]);
    const [loadingDataElements, setLoadingDataElements] = useState(false);
    const [dataElementsSearchTerm, setDataElementsSearchTerm] = useState('');
    const [addingDataElementId, setAddingDataElementId] = useState<number | null>(null);
    const [removingDataElementId, setRemovingDataElementId] = useState<number | null>(null);
    const [dataElementsChanged, setDataElementsChanged] = useState(false);

    const DRAWER_WIDTH = theme.customLayout.drawerWidth;
    const HEADER_HEIGHT = theme.customLayout.headerHeight;
    const MEDIUM_GAP = theme.customLayout.mediumGap;

    useEffect(() => {
        if (purpose) {
            const initialValues = {
                name: purpose.name || '',
                description: purpose.description || ''
            };
            setInitialValues(initialValues);
            setName(initialValues.name);
            setDescription(initialValues.description);
        }
    }, [purpose, open]);

    // Load languages
    useEffect(() => {
        if (!open) {
            setCurrentTab(0);
            setActiveLanguage('en');
            return;
        }

        // Load supported languages
        languageService.getSupportedLanguages()
            .then(languages => {
                setLanguages(languages);
                // Set the first non-English language as active if available
                const firstNonEnglishLang = languages.find(lang => lang.code !== 'en');
                if (firstNonEnglishLang) {
                    setActiveLanguage(firstNonEnglishLang.code);
                }
            })
            .catch(error => {
                console.error('Failed to fetch supported languages:', error);
                setError('Failed to load supported languages');
            });
    }, [open]);

    // Initialize translations after languages are loaded
    useEffect(() => {
        if (!open || !purpose || languages.length === 0) return;

        const initialTranslations: Record<string, { name: string; description: string }> = {
            en: {
                name: purpose.name || '',
                description: purpose.description || ''
            }
        };

        // Initialize empty translations for all other languages
        languages.forEach(lang => {
            if (lang.code !== 'en') {
                initialTranslations[lang.code] = {
                    name: '',
                    description: ''
                };
            }
        });

        setTranslations(initialTranslations);
        setOriginalTranslations(initialTranslations);
        setAcceptedTranslations(new Set(['en'])); // English is always accepted
        setShowStartTranslation(true);

        // Load existing translations from API
        purposeService.getPurposeById(purpose.purposeId)
            .then(purposeDetail => {
                if (purposeDetail) {
                    // Initialize with English values
                    const newTranslations: Record<string, { name: string; description: string }> = {
                        en: {
                            name: purposeDetail.name || '',
                            description: purposeDetail.description || ''
                        }
                    };
                    
                    // Initialize empty translations for all other languages
                    languages.forEach(lang => {
                        if (lang.code !== 'en') {
                            newTranslations[lang.code] = {
                                name: '',
                                description: ''
                            };
                        }
                    });

                    // Populate with existing translations from server
                    if (purposeDetail.translations) {
                        purposeDetail.translations.forEach(translation => {
                            if (translation.languageCode !== 'en') {
                                newTranslations[translation.languageCode] = {
                                    name: translation.translatedName || '',
                                    description: translation.translatedDescription || ''
                                };
                            }
                        });
                    }
                    
                    setTranslations(newTranslations);
                    setOriginalTranslations(newTranslations);
                    
                    // Set accepted translations based on existing translations
                    const acceptedLangs = new Set(['en']); // English is always accepted
                    if (purposeDetail.translations) {
                        purposeDetail.translations.forEach(translation => {
                            acceptedLangs.add(translation.languageCode);
                        });
                    }
                    setAcceptedTranslations(acceptedLangs);
                    
                    // Only show start translation screen if there are no existing translations
                    const hasExistingTranslations = purposeDetail.translations && purposeDetail.translations.length > 0;
                    setShowStartTranslation(!hasExistingTranslations);
                }
            })
            .catch(error => {
                console.error('Failed to load purpose details:', error);
                // Use the initial translations if API call fails
                setTranslations(initialTranslations);
                setOriginalTranslations(initialTranslations);
                setAcceptedTranslations(new Set(['en'])); // English is always accepted
                
                // Check if purpose has existing translations (from the original purpose prop)
                const hasExistingTranslations = purpose.translations && purpose.translations.length > 0;
                setShowStartTranslation(!hasExistingTranslations);
            });
    }, [purpose, open, languages]);

    // Load preferences data when preferences tab is opened
    useEffect(() => {
        if (!open || !purpose || currentTab !== 2) return;

        const loadPreferencesData = async () => {
            setLoadingPreferences(true);
            try {
                // Load mapped preferences
                const mappedPrefs = await purposeService.getPurposePreferences(purpose.purposeId);
                setMappedPreferences(mappedPrefs);

                // Load all available published preferences
                const allPrefs = await dpOptionsService.getDPOptions({
                    filters: { isPublished: true },
                    pagination: { page: 1, pageSize: 1000 }, // Large page size to get all
                    sortBy: 'A-Z'
                });

                // Filter out already mapped preferences
                const mappedPreferenceIds = new Set(mappedPrefs.map(p => p.purposePreferenceId));
                const availablePrefs = allPrefs.data.filter(pref => !mappedPreferenceIds.has(pref.id));
                setAvailablePreferences(availablePrefs);
            } catch (error) {
                console.error('Failed to load preferences data:', error);
                setError('Failed to load preferences data');
            } finally {
                setLoadingPreferences(false);
            }
        };

        loadPreferencesData();
    }, [purpose, open, currentTab]);

    // Load processors data when processors tab is opened
    useEffect(() => {
        if (!open || !purpose || currentTab !== 3) return;

        const loadProcessorsData = async () => {
            setLoadingProcessors(true);
            try {
                // Load mapped processors
                const mappedProcs = await purposeService.getPurposeProcessors(purpose.purposeId);
                setMappedProcessors(mappedProcs);

                // Load all available published processors
                const allProcs = await tppService.getProcessors({
                    filters: { isPublished: true },
                    pagination: { page: 1, pageSize: 1000 }, // Large page size to get all
                    sortBy: 'a-z'
                });

                // Filter out already mapped processors
                const mappedProcessorIds = new Set(mappedProcs.map(p => p.processorId).filter(id => id !== undefined));
                const availableProcs = allProcs.processors.filter(proc => proc.processorId && !mappedProcessorIds.has(proc.processorId));
                setAvailableProcessors(availableProcs);
            } catch (error) {
                console.error('Failed to load processors data:', error);
                setError('Failed to load processors data');
            } finally {
                setLoadingProcessors(false);
            }
        };

        loadProcessorsData();
    }, [purpose, open, currentTab]);

    // Load data elements data when data elements tab is opened
    useEffect(() => {
        if (!open || !purpose || currentTab !== 4) return;

        const loadDataElementsData = async () => {
            setLoadingDataElements(true);
            try {
                // Load mapped data elements
                const mappedElements = await purposeService.getPurposeDataElements(purpose.purposeId);
                setMappedDataElements(mappedElements);

                // Load all available published data elements
                const allElements = await dataElementsService.getDataElements(
                    { isPublished: true },
                    { page: 1, pageSize: 1000 }, // Large page size to get all
                    'a-z'
                );

                // Filter out already mapped data elements
                const mappedElementIds = new Set(mappedElements.map(e => e.elementId).filter(id => id !== undefined));
                const availableElements = allElements.elements.filter(element => element.elementId && !mappedElementIds.has(element.elementId));
                setAvailableDataElements(availableElements);
            } catch (error) {
                console.error('Failed to load data elements data:', error);
                setError('Failed to load data elements data');
            } finally {
                setLoadingDataElements(false);
            }
        };

        loadDataElementsData();
    }, [purpose, open, currentTab]);

    const hasDetailsChanged = () => {
        if (!initialValues) return false;
        return (
            name !== initialValues.name ||
            description !== initialValues.description
        );
    };

    const handleSaveDetails = async () => {
        if (!purpose) return;
        setSaving(true);
        setError(null);
        try {
            await purposeService.updatePurpose(purpose.purposeId, {
                name,
                description
            });
            setSaveSuccess(true);
            
            setInitialValues({
                name,
                description
            });
            if (onSave) {
                onSave();
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to update purpose');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (preferencesChanged || processorsChanged || dataElementsChanged) {
            if (onSave) onSave();
            setPreferencesChanged(false);
            setProcessorsChanged(false);
            setDataElementsChanged(false);
        }
        onClose();
    };

    const handleCancelClose = () => {
        setShowCloseConfirm(false);
    };

    const handlePublishToggle = async () => {
        if (!purpose) return;

        const isCurrentlyPublished = purpose.isPublished;

        if (isCurrentlyPublished) {
            // If already published, show confirmation for unpublishing
            const confirmUnpublish = window.confirm(
                'Are you sure you want to unpublish this purpose?\n\n' +
                'Unpublishing will make this purpose unavailable to other parts of the program.\n' +
                'You can publish it again later when needed.'
            );

            if (!confirmUnpublish) {
                return;
            }

            try {
                setIsPublishing(true);
                await purposeService.unpublishPurpose(purpose.purposeId);
                onSave?.();
                onClose(); // Close the modal after successful unpublish
            } catch (error: any) {
                setError(error.response?.data?.message || 'Failed to unpublish purpose');
            } finally {
                setIsPublishing(false);
            }
            return;
        }

        // Step 1: Check for unsaved changes in both tabs
        const hasDetailsChanges = hasDetailsChanged();
        const hasTranslationChanges = hasAnyTranslationChanges();

        if (hasDetailsChanges || hasTranslationChanges) {
            setError('Please save all changes before publishing the purpose');
            return;
        }

        // Step 2: Check for missing translations
        const missingTranslations: string[] = [];
        const unacceptedTranslations: string[] = [];

        languages.forEach(lang => {
            if (lang.code === 'en') return; // Skip English as it's the source

            const translation = translations[lang.code];
            const isAccepted = acceptedTranslations.has(lang.code);

            if (!translation || (!translation.name && !translation.description)) {
                missingTranslations.push(lang.name);
            } else if (!isAccepted) {
                unacceptedTranslations.push(lang.name);
            }
        });

        // Step 3: Build confirmation message
        let confirmMessage = `Are you sure you want to publish this purpose?\n\n` +
            `Publishing will make this purpose available to other parts of the program to use.\n\n` +
            `Please ensure that you have:\n` +
            `• Reviewed all translations\n` +
            `• Verified the purpose name and description\n` +
            `• Configured all necessary preferences, processors, and data elements\n\n`;

        // Add translation warnings if any
        if (missingTranslations.length > 0 || unacceptedTranslations.length > 0) {
            confirmMessage += `⚠️  WARNING:\n`;
            
            if (missingTranslations.length > 0) {
                confirmMessage += `• ${missingTranslations.length} language(s) do not have translations\n`;
            }
            
            if (unacceptedTranslations.length > 0) {
                confirmMessage += `• ${unacceptedTranslations.length} language(s) have unaccepted translations\n`;
            }
            
            confirmMessage += `\nYou can still publish, but these translations will not be available.\n\n`;
        }

        confirmMessage += `This action cannot be undone.`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        // If user confirms, publish the purpose
        try {
            setIsPublishing(true);
            await purposeService.publishPurpose(purpose.purposeId);
            onSave?.();
            onClose(); // Close the modal after successful publish
        } catch (error: any) {
            setError(error.response?.data?.message || 'Failed to publish purpose');
        } finally {
            setIsPublishing(false);
        }
    };

    const handleTranslateAI = async () => {
        if (!translations['en']?.name && !translations['en']?.description) {
            setError('Please ensure English text is available for translation');
            return;
        }

        setIsTranslating(true);
        setError(null);

        try {
            const fields: { [key: string]: string } = {};
            if (translations['en']?.name) fields['name'] = translations['en'].name;
            if (translations['en']?.description) fields['description'] = translations['en'].description;

            const result = await translationService.translateBatch(fields, activeLanguage);

            setTranslations(prev => ({
                ...prev,
                [activeLanguage]: {
                    name: result['name'] || '',
                    description: result['description'] || ''
                }
            }));
        } catch (error: any) {
            console.error('Translation error:', error);
            setError(error.response?.data?.message || 'Failed to translate text');
        } finally {
            setIsTranslating(false);
        }
    };

    const handleTranslationChange = (field: 'name' | 'description', value: string) => {
        setTranslations(prev => ({
            ...prev,
            [activeLanguage]: {
                ...prev[activeLanguage],
                [field]: value,
            },
        }));
    };

    const hasTranslationChanges = () => {
        const current = translations[activeLanguage];
        const original = originalTranslations[activeLanguage];
        if (!current) return false;
        
        // If there's no original translation, check if current has content
        if (!original) {
            return (current.name || '').trim() !== '' || (current.description || '').trim() !== '';
        }
        
        return current.name !== original.name || current.description !== original.description;
    };

    const hasAnyTranslationChanges = () => {
        return Object.keys(translations).some(langCode => {
            if (langCode === 'en') return false; // Skip English as it's the source
            const current = translations[langCode];
            const original = originalTranslations[langCode];
            if (!current) return false;
            
            // If there's no original translation, check if current has content
            if (!original) {
                return (current.name || '').trim() !== '' || (current.description || '').trim() !== '';
            }
            
            return current.name !== original.name || current.description !== original.description;
        });
    };

    const handleAcceptTranslation = async () => {
        if (!purpose?.purposeId || !hasTranslationChanges()) {
            setError('Please ensure there are changes to save');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            await purposeService.updateTranslation(
                purpose.purposeId,
                {
                    languageCode: activeLanguage,
                    translatedName: translations[activeLanguage]?.name || '',
                    translatedDescription: translations[activeLanguage]?.description || ''
                }
            );

            // Add to accepted translations and update original
            setAcceptedTranslations(prev => new Set(Array.from(prev).concat(activeLanguage)));
            setOriginalTranslations(prev => ({
                ...prev,
                [activeLanguage]: {
                    name: translations[activeLanguage]?.name || '',
                    description: translations[activeLanguage]?.description || ''
                }
            }));

        } catch (error: any) {
            console.error('Error saving translation:', error);
            setError(error.response?.data?.message || 'Failed to save translation');
        } finally {
            setSaving(false);
        }
    };

    // Preferences helper functions
    const filteredAvailablePreferences = availablePreferences.filter(pref =>
        pref.name.toLowerCase().includes(preferencesSearchTerm.toLowerCase()) ||
        (pref.description && pref.description.toLowerCase().includes(preferencesSearchTerm.toLowerCase()))
    );

    const handleAddPreference = async (preferenceId: number) => {
        if (!purpose) return;
        
        setAddingPreferenceId(preferenceId);
        try {
            await purposeService.addPurposePreference(purpose.purposeId, preferenceId);
            
            // Refresh preferences data
            const mappedPrefs = await purposeService.getPurposePreferences(purpose.purposeId);
            setMappedPreferences(mappedPrefs);
            
            // Remove the added preference from available preferences
            setAvailablePreferences(prev => prev.filter(pref => pref.id !== preferenceId));
            
            setPreferencesChanged(true);
            setSaveSuccess(true);
        } catch (error: any) {
            console.error('Failed to add preference:', error);
            setError(error.response?.data?.message || 'Failed to add preference');
        } finally {
            setAddingPreferenceId(null);
        }
    };

    const handleRemovePreference = async (preferenceId: number) => {
        if (!purpose) return;
        
        setRemovingPreferenceId(preferenceId);
        try {
            await purposeService.removePurposePreference(purpose.purposeId, preferenceId);
            
            // Refresh preferences data
            const mappedPrefs = await purposeService.getPurposePreferences(purpose.purposeId);
            setMappedPreferences(mappedPrefs);
            
            // Get the removed preference data from DPOptions service and add it back to available preferences
            try {
                const allPrefs = await dpOptionsService.getDPOptions({
                    filters: { isPublished: true },
                    pagination: { page: 1, pageSize: 1000 },
                    sortBy: 'A-Z'
                });
                
                const removedPreference = allPrefs.data.find(p => p.id === preferenceId);
                if (removedPreference) {
                    setAvailablePreferences(prev => [...prev, removedPreference]);
                }
            } catch (error) {
                console.error('Failed to refresh available preferences:', error);
            }
            
            setPreferencesChanged(true);
            setSaveSuccess(true);
        } catch (error: any) {
            console.error('Failed to remove preference:', error);
            setError(error.response?.data?.message || 'Failed to remove preference');
        } finally {
            setRemovingPreferenceId(null);
        }
    };

    // Processors helper functions
    const filteredAvailableProcessors = availableProcessors.filter(proc =>
        proc.name.toLowerCase().includes(processorsSearchTerm.toLowerCase()) ||
        (proc.description && proc.description.toLowerCase().includes(processorsSearchTerm.toLowerCase()))
    );

    const handleAddProcessor = async (processorId: number) => {
        if (!purpose) return;
        
        setAddingProcessorId(processorId);
        try {
            await purposeService.addPurposeProcessor(purpose.purposeId, processorId);
            
            // Refresh processors data
            const mappedProcs = await purposeService.getPurposeProcessors(purpose.purposeId);
            setMappedProcessors(mappedProcs);
            
            // Remove the added processor from available processors
            setAvailableProcessors(prev => prev.filter(proc => proc.processorId !== processorId));
            
            setProcessorsChanged(true);
            setSaveSuccess(true);
        } catch (error: any) {
            console.error('Failed to add processor:', error);
            setError(error.response?.data?.message || 'Failed to add processor');
        } finally {
            setAddingProcessorId(null);
        }
    };

    const handleRemoveProcessor = async (processorId: number) => {
        if (!purpose) return;
        
        setRemovingProcessorId(processorId);
        try {
            await purposeService.removePurposeProcessor(purpose.purposeId, processorId);
            
            // Refresh processors data
            const mappedProcs = await purposeService.getPurposeProcessors(purpose.purposeId);
            setMappedProcessors(mappedProcs);
            
            // Get the removed processor data from TPP service and add it back to available processors
            try {
                const allProcs = await tppService.getProcessors({
                    filters: { isPublished: true },
                    pagination: { page: 1, pageSize: 1000 },
                    sortBy: 'a-z'
                });
                
                const removedProcessor = allProcs.processors.find(p => p.processorId === processorId);
                if (removedProcessor) {
                    setAvailableProcessors(prev => [...prev, removedProcessor]);
                }
            } catch (error) {
                console.error('Failed to refresh available processors:', error);
            }
            
            setProcessorsChanged(true);
            setSaveSuccess(true);
        } catch (error: any) {
            console.error('Failed to remove processor:', error);
            setError(error.response?.data?.message || 'Failed to remove processor');
        } finally {
            setRemovingProcessorId(null);
        }
    };

    // Data Elements helper functions
    const filteredAvailableDataElements = availableDataElements.filter(element =>
        element.elementName.toLowerCase().includes(dataElementsSearchTerm.toLowerCase()) ||
        (element.description && element.description.toLowerCase().includes(dataElementsSearchTerm.toLowerCase())) ||
        element.elementCode.toLowerCase().includes(dataElementsSearchTerm.toLowerCase())
    );

    const handleAddDataElement = async (elementId: number) => {
        if (!purpose) return;
        
        setAddingDataElementId(elementId);
        try {
            await purposeService.addPurposeDataElement(purpose.purposeId, elementId);
            
            // Refresh data elements data
            const mappedElements = await purposeService.getPurposeDataElements(purpose.purposeId);
            setMappedDataElements(mappedElements);
            
            // Remove the added data element from available data elements
            setAvailableDataElements(prev => prev.filter(element => element.elementId !== elementId));
            
            setDataElementsChanged(true);
            setSaveSuccess(true);
        } catch (error: any) {
            console.error('Failed to add data element:', error);
            setError(error.response?.data?.message || 'Failed to add data element');
        } finally {
            setAddingDataElementId(null);
        }
    };

    const handleRemoveDataElement = async (elementId: number) => {
        if (!purpose) return;
        
        setRemovingDataElementId(elementId);
        try {
            await purposeService.removePurposeDataElement(purpose.purposeId, elementId);
            
            // Refresh data elements data
            const mappedElements = await purposeService.getPurposeDataElements(purpose.purposeId);
            setMappedDataElements(mappedElements);
            
            // Get the removed data element data from DataElements service and add it back to available data elements
            try {
                const allElements = await dataElementsService.getDataElements(
                    { isPublished: true },
                    { page: 1, pageSize: 1000 },
                    'a-z'
                );
                
                const removedElement = allElements.elements.find(e => e.elementId === elementId);
                if (removedElement) {
                    setAvailableDataElements(prev => [...prev, removedElement]);
                }
            } catch (error) {
                console.error('Failed to refresh available data elements:', error);
            }
            
            setDataElementsChanged(true);
            setSaveSuccess(true);
        } catch (error: any) {
            console.error('Failed to remove data element:', error);
            setError(error.response?.data?.message || 'Failed to remove data element');
        } finally {
            setRemovingDataElementId(null);
        }
    };

    return (
        <>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="edit-purpose-modal-title"
                sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', zIndex: 1301 }}
            >
                <Box
                    sx={{
                        bgcolor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        boxShadow: theme.shadows[24],
                        p: theme.spacing(0),
                        width: `calc(100vw - ${DRAWER_WIDTH + 2 * MEDIUM_GAP}px)`,
                        height: `calc(100vh - ${HEADER_HEIGHT + 2 * MEDIUM_GAP}px)`,
                        position: 'absolute',
                        left: `${DRAWER_WIDTH + MEDIUM_GAP}px`,
                        top: `${HEADER_HEIGHT + MEDIUM_GAP}px`,
                        right: `${MEDIUM_GAP}px`,
                        bottom: `${MEDIUM_GAP}px`,
                        display: 'flex',
                        flexDirection: 'column',
                        outline: 'none',
                    }}
                >
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        alignItems: 'center',
                    }}>
                        <Typography id="edit-purpose-modal-title" variant="h6" sx={{ color: theme.palette.text.primary }}>
                            {purpose?.isPublished ? 'View Purpose' : 'Edit Purpose'}
                            {purpose?.name && (
                                <span style={{ color: theme.palette.text.secondary, fontWeight: 'normal' }}>
                                    : {purpose.name}
                                </span>
                            )}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handlePublishToggle}
                                disabled={isPublishing || saving || isDeleting}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        bgcolor: theme.palette.primary.dark,
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: theme.palette.action.disabledBackground,
                                        color: theme.palette.action.disabled,
                                    }
                                }}
                            >
                                {isPublishing ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    purpose?.isPublished ? 'Unpublish Purpose' : 'Publish Purpose'
                                )}
                            </Button>
                            {purpose?.isPublished ? (
                                <Button 
                                    variant="outlined" 
                                    color="inherit" 
                                    onClick={handleClose}
                                    sx={{
                                        borderColor: theme.palette.divider,
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                            bgcolor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                            ) : (
                                <IconButton
                                    onClick={handleClose}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': {
                                            bgcolor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            )}
                        </Stack>
                    </Box>

                    {/* Tabs */}
                    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Tabs
                            value={currentTab}
                            onChange={(_, v) => setCurrentTab(v)}
                            sx={{
                                '& .MuiTab-root': {
                                    color: theme.palette.text.secondary,
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            <Tab 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        Details
                                        {hasDetailsChanged() && (
                                            <Typography 
                                                component="span" 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    opacity: 0.8,
                                                    fontSize: '1.2rem',
                                                    lineHeight: 1,
                                                    ml: 0.5,
                                                    fontWeight: 500
                                                }}
                                            >
                                                •
                                            </Typography>
                                        )}
                                    </Box>
                                } 
                            />
                            <Tab 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Translations
                                        {currentTab === 1 && hasAnyTranslationChanges() && (
                                            <Typography 
                                                component="span" 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    opacity: 0.8,
                                                    fontSize: '1.2rem',
                                                    lineHeight: 1,
                                                    ml: 0.5,
                                                    fontWeight: 500
                                                }}
                                            >
                                                •
                                            </Typography>
                                        )}
                                    </Box>
                                } 
                            />
                            <Tab label="Preferences" />
                            <Tab label="Processors" />
                            <Tab label="Data Elements" />
                        </Tabs>
                    </Box>

                    {/* Tab Panels */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {currentTab === 0 && (
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="Name"
                                        fullWidth
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        disabled={purpose?.isPublished}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Description"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        disabled={purpose?.isPublished}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                            },
                                        }}
                                    />

                                    {/* Danger Zone */}
                                    {!purpose?.isPublished && (
                                        <Box sx={{ 
                                            mt: 4, 
                                            p: 3, 
                                            border: `1px solid ${theme.palette.error.main}`,
                                            borderRadius: theme.shape.borderRadius,
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.08)' : 'rgba(211, 47, 47, 0.04)',
                                        }}>
                                            <Typography variant="h6" sx={{ color: theme.palette.error.main, mb: 1 }}>
                                                Danger Zone
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.palette.error.main, mb: 2 }}>
                                                Purpose once deleted cannot be retrieved
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                onClick={async () => {
                                                    if (window.confirm('Are you sure you want to delete this purpose? This action cannot be undone.')) {
                                                        if (purpose?.purposeId) {
                                                            try {
                                                                setIsDeleting(true);
                                                                await purposeService.deletePurpose(purpose.purposeId);
                                                                onClose();
                                                                if (onSave) {
                                                                    onSave();
                                                                }
                                                            } catch (error: any) {
                                                                // Check if it's a permission error
                                                                if (error.response?.status === 403) {
                                                                    setError('You do not have permission to delete this purpose');
                                                                } else if (error.response?.status === 404) {
                                                                    setError('Purpose not found');
                                                                } else {
                                                                    setError(error.response?.data?.message || 'Failed to delete purpose');
                                                                }
                                                            } finally {
                                                                setIsDeleting(false);
                                                            }
                                                        }
                                                    }
                                                }}
                                                disabled={isDeleting || saving}
                                                sx={{
                                                    bgcolor: theme.palette.error.main,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.error.dark,
                                                    },
                                                }}
                                            >
                                                {isDeleting ? (
                                                    <CircularProgress size={24} color="inherit" />
                                                ) : (
                                                    'Delete Purpose'
                                                )}
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )}
                        {currentTab === 1 && showStartTranslation ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                textAlign: 'center',
                            }}>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    Use this space to translate the Purpose in multiple languages.<br />
                                    Please ensure that you have finalized the text in the default language before proceeding.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="success"
                                    sx={{ mb: 2, px: 4 }}
                                    onClick={() => {
                                        setShowStartTranslation(false);
                                        if (languages.length > 0) {
                                            const firstNonEnglishLang = languages.find(lang => lang.code !== 'en');
                                            if (firstNonEnglishLang) {
                                                setActiveLanguage(firstNonEnglishLang.code);
                                            }
                                        }
                                    }}
                                >
                                    START TRANSLATION
                                </Button>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    PS. We provide translation using AI :)
                                </Typography>
                            </Box>
                        ) : null}

                        {currentTab === 1 && !showStartTranslation && (
                            <Box sx={{ p: 3, display: 'flex', gap: 3, height: `calc(100% - ${MEDIUM_GAP}px)` }}>
                                {/* Table-like language list */}
                                <Box sx={{ 
                                    minWidth: 380, 
                                    height: '100%', 
                                    maxHeight: '100%', 
                                    overflowY: 'auto', 
                                    border: `1px solid ${theme.palette.divider}`, 
                                    borderRadius: theme.shape.borderRadius, 
                                    p: 0, 
                                    bgcolor: theme.palette.background.paper, 
                                    mb: `${MEDIUM_GAP}px`, 
                                    display: 'flex', 
                                    flexDirection: 'column' 
                                }}>
                                    {/* Header Row */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        px: 2, 
                                        py: 1, 
                                        borderBottom: `1px solid ${theme.palette.divider}`, 
                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50], 
                                        fontWeight: 600 
                                    }}>
                                        <Typography variant="body2" sx={{ flex: 2, fontWeight: 600, color: theme.palette.text.primary }}>Languages</Typography>
                                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, color: theme.palette.text.primary }}>Code</Typography>
                                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, textAlign: 'center', color: theme.palette.text.primary }}>Accepted</Typography>
                                    </Box>
                                    {/* Language Rows */}
                                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                        {languages.map((lang) => {
                                            const isSelected = activeLanguage === lang.code;
                                            const isNotSupported = lang.translationProvider === null;
                                            const rowContent = (
                                                <Box
                                                    key={lang.code}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 2,
                                                        py: 1,
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: isSelected ? theme.palette.action.selected : 'inherit',
                                                        opacity: isNotSupported ? 0.5 : 1,
                                                        transition: 'background 0.2s',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: isNotSupported ? undefined : theme.palette.action.hover,
                                                        },
                                                    }}
                                                    onClick={() => setActiveLanguage(lang.code)}
                                                >
                                                    <Box sx={{ flex: 2 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline', color: theme.palette.text.primary }}>{lang.name}</Typography>
                                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary, display: 'block', ml: 0.5 }}>
                                                            {lang.localeName}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>{lang.code.toUpperCase()}</Typography>
                                                        {lang.code !== 'en' && (() => {
                                                            const current = translations[lang.code];
                                                            const original = originalTranslations[lang.code];
                                                            if (!current || !original) return null;
                                                            const hasChanges = current.name !== original.name || current.description !== original.description;
                                                            return hasChanges ? (
                                                                <Typography 
                                                                    component="span" 
                                                                    sx={{ 
                                                                        color: theme.palette.primary.main,
                                                                        fontSize: '0.875rem',
                                                                        lineHeight: 1,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    •
                                                                </Typography>
                                                            ) : null;
                                                        })()}
                                                    </Box>
                                                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                                        <Checkbox
                                                            checked={acceptedTranslations.has(lang.code)}
                                                            readOnly
                                                            size="small"
                                                            sx={{ p: 0 }}
                                                        />
                                                    </Box>
                                                </Box>
                                            );
                                            return isNotSupported ? (
                                                <Tooltip key={lang.code} title="This language is not supported by AI" arrow>
                                                    <span style={{ display: 'block' }}>{rowContent}</span>
                                                </Tooltip>
                                            ) : rowContent;
                                        })}
                                    </Box>
                                </Box>

                                {/* Translation form for selected language */}
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ mb: 2, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                                            English Reference
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}>
                                            Name: {translations['en']?.name || ''}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                            Description: {translations['en']?.description || ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                                            Language: {languages.find((l) => l.code === activeLanguage)?.name || activeLanguage}
                                        </Typography>
                                        {!purpose?.isPublished && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                endIcon={isTranslating ? <CircularProgress size={20} /> : <img src={AIIcon} alt="AI" style={{ width: 20, height: 20 }} />}
                                                sx={{
                                                    ml: 2,
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main,
                                                    borderColor: theme.palette.primary.main,
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.dark,
                                                        color: theme.palette.primary.dark,
                                                    },
                                                }}
                                                disabled={languages.find((l) => l.code === activeLanguage)?.translationProvider === null || isTranslating}
                                                onClick={handleTranslateAI}
                                            >
                                                {isTranslating ? 'TRANSLATING...' : 'TRANSLATE USING AI'}
                                            </Button>
                                        )}
                                        <Tooltip
                                            title="AI-generated translations may contain errors or inaccuracies. Please review and cross-check the translation before accepting."
                                            arrow
                                        >
                                            <InfoOutlinedIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    ml: 1, 
                                                    color: theme.palette.neutral ? theme.palette.neutral.main : theme.palette.text.secondary, 
                                                    cursor: 'pointer',
                                                    fontWeight: 400
                                                }} 
                                            />
                                        </Tooltip>
                                    </Box>
                                    <TextField
                                        label="Name"
                                        fullWidth
                                        size="small"
                                        required
                                        value={translations[activeLanguage]?.name || ''}
                                        onChange={e => handleTranslationChange('name', e.target.value)}
                                        disabled={purpose?.isPublished}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                            },
                                        }}
                                    />
                                    <TextField
                                        label="Description"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        size="small"
                                        value={translations[activeLanguage]?.description || ''}
                                        onChange={e => handleTranslationChange('description', e.target.value)}
                                        disabled={purpose?.isPublished}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.secondary,
                                            },
                                        }}
                                    />
                                    {!purpose?.isPublished && (
                                        <Button 
                                            variant="contained" 
                                            sx={{ 
                                                alignSelf: 'flex-start', 
                                                mt: 2,
                                                bgcolor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                '&:hover': {
                                                    bgcolor: theme.palette.primary.dark,
                                                },
                                            }}
                                            onClick={handleAcceptTranslation}
                                            disabled={saving || !hasTranslationChanges()}
                                        >
                                            {saving ? <CircularProgress size={24} color="inherit" /> : 'Accept'}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        )}
                        {currentTab === 2 && (
                            <Box sx={{ p: 3 }}>
                                {loadingPreferences ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Grid container spacing={3}>
                                        {/* Mapped Preferences */}
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <CardContent sx={{ flex: 1, p: 0 }}>
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: theme.palette.background.default
                                                    }}>
                                                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                                            Mapped Preferences ({mappedPreferences.length})
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 500 }}>
                                                        {mappedPreferences.length === 0 ? (
                                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    No preferences mapped to this purpose yet.
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            mappedPreferences.map((mapping, index) => (
                                                                <Box key={mapping.mappingId}>
                                                                    <Box sx={{ 
                                                                        p: 2, 
                                                                        borderBottom: index < mappedPreferences.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                                        '&:hover': {
                                                                            bgcolor: theme.palette.action.hover
                                                                        }
                                                                    }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                            <Typography variant="subtitle1" sx={{ 
                                                                                color: theme.palette.text.primary, 
                                                                                fontWeight: 600,
                                                                                flex: 1
                                                                            }}>
                                                                                {mapping.preference.name}
                                                                            </Typography>
                                                                            {!purpose?.isPublished && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleRemovePreference(mapping.purposePreferenceId)}
                                                                                    disabled={removingPreferenceId === mapping.purposePreferenceId}
                                                                                    sx={{
                                                                                        color: theme.palette.error.main,
                                                                                        bgcolor: theme.palette.error.light,
                                                                                        '&:hover': {
                                                                                            bgcolor: theme.palette.error.main,
                                                                                            color: theme.palette.error.contrastText,
                                                                                        },
                                                                                        '&:disabled': {
                                                                                            bgcolor: theme.palette.action.disabledBackground,
                                                                                            color: theme.palette.action.disabled
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {removingPreferenceId === mapping.purposePreferenceId ? (
                                                                                        <CircularProgress size={16} color="inherit" />
                                                                                    ) : (
                                                                                        <RemoveIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                        
                                                                        {mapping.preference.description && (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                                {mapping.preference.description}
                                                                            </Typography>
                                                                        )}
                                                                        
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Display:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{mapping.preference.displayAs}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Multiple:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{mapping.preference.allowMultipleSelections ? 'Yes' : 'No'}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Default:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{mapping.preference.setADefaultSelection ? 'Yes' : 'No'}</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Available Preferences */}
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <CardContent sx={{ flex: 1, p: 0 }}>
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: theme.palette.background.default
                                                    }}>
                                                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                                            Available Preferences ({filteredAvailablePreferences.length})
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Search preferences..."
                                                            value={preferencesSearchTerm}
                                                            onChange={(e) => setPreferencesSearchTerm(e.target.value)}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 400 }}>
                                                        {filteredAvailablePreferences.length === 0 ? (
                                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    {preferencesSearchTerm ? 'No preferences match your search.' : 'No available preferences to add.'}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            filteredAvailablePreferences.map((pref, index) => (
                                                                <Box key={pref.id}>
                                                                    <Box sx={{ 
                                                                        p: 2, 
                                                                        borderBottom: index < filteredAvailablePreferences.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                                        '&:hover': {
                                                                            bgcolor: theme.palette.action.hover
                                                                        }
                                                                    }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                            <Typography variant="subtitle1" sx={{ 
                                                                                color: theme.palette.text.primary, 
                                                                                fontWeight: 600,
                                                                                flex: 1
                                                                            }}>
                                                                                {pref.name}
                                                                            </Typography>
                                                                            {!purpose?.isPublished && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleAddPreference(pref.id)}
                                                                                    disabled={addingPreferenceId === pref.id}
                                                                                    sx={{
                                                                                        color: theme.palette.success.main,
                                                                                        bgcolor: theme.palette.error.light,
                                                                                        '&:hover': {
                                                                                            bgcolor: theme.palette.success.main,
                                                                                            color: theme.palette.success.contrastText,
                                                                                        },
                                                                                        '&:disabled': {
                                                                                            bgcolor: theme.palette.action.disabledBackground,
                                                                                            color: theme.palette.action.disabled
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {addingPreferenceId === pref.id ? (
                                                                                        <CircularProgress size={16} color="inherit" />
                                                                                    ) : (
                                                                                        <AddIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                        
                                                                        {pref.description && (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                                {pref.description}
                                                                            </Typography>
                                                                        )}
                                                                        
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Display:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{pref.displayAs}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Multiple:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{pref.allowMultipleSelection ? 'Yes' : 'No'}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Default:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{pref.setADefaultSelection ? 'Yes' : 'No'}</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>
                        )}
                        {currentTab === 3 && (
                            <Box sx={{ p: 3 }}>
                                {loadingProcessors ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Grid container spacing={3}>
                                        {/* Mapped Processors */}
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <CardContent sx={{ flex: 1, p: 0 }}>
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: theme.palette.background.default
                                                    }}>
                                                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                                            Mapped Processors ({mappedProcessors.length})
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 500 }}>
                                                        {mappedProcessors.length === 0 ? (
                                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    No processors mapped to this purpose yet.
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            mappedProcessors.map((mapping, index) => (
                                                                <Box key={mapping.mappingId}>
                                                                    <Box sx={{ 
                                                                        p: 2, 
                                                                        borderBottom: index < mappedProcessors.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                                        '&:hover': {
                                                                            bgcolor: theme.palette.action.hover
                                                                        }
                                                                    }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                            <Typography variant="subtitle1" sx={{ 
                                                                                color: theme.palette.text.primary, 
                                                                                fontWeight: 600,
                                                                                flex: 1
                                                                            }}>
                                                                                {mapping.processor.name}
                                                                            </Typography>
                                                                            {!purpose?.isPublished && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleRemoveProcessor(mapping.processorId)}
                                                                                    disabled={removingProcessorId === mapping.processorId}
                                                                                    sx={{
                                                                                        color: theme.palette.error.main,
                                                                                        bgcolor: theme.palette.error.light,
                                                                                        '&:hover': {
                                                                                            bgcolor: theme.palette.error.main,
                                                                                            color: theme.palette.error.contrastText,
                                                                                        },
                                                                                        '&:disabled': {
                                                                                            bgcolor: theme.palette.action.disabledBackground,
                                                                                            color: theme.palette.action.disabled
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {removingProcessorId === mapping.processorId ? (
                                                                                        <CircularProgress size={16} color="inherit" />
                                                                                    ) : (
                                                                                        <RemoveIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                        
                                                                        {mapping.processor.description && (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                                {mapping.processor.description}
                                                                            </Typography>
                                                                        )}
                                                                        
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Type:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{mapping.processor.type}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Website:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{mapping.processor.website || 'N/A'}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Email:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{mapping.processor.contactEmail || 'N/A'}</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Available Processors */}
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <CardContent sx={{ flex: 1, p: 0 }}>
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: theme.palette.background.default
                                                    }}>
                                                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                                            Available Processors ({filteredAvailableProcessors.length})
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Search processors..."
                                                            value={processorsSearchTerm}
                                                            onChange={(e) => setProcessorsSearchTerm(e.target.value)}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 400 }}>
                                                        {filteredAvailableProcessors.length === 0 ? (
                                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    {processorsSearchTerm ? 'No processors match your search.' : 'No available processors to add.'}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            filteredAvailableProcessors.map((proc, index) => (
                                                                <Box key={proc.processorId}>
                                                                    <Box sx={{ 
                                                                        p: 2, 
                                                                        borderBottom: index < filteredAvailableProcessors.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                                        '&:hover': {
                                                                            bgcolor: theme.palette.action.hover
                                                                        }
                                                                    }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                            <Typography variant="subtitle1" sx={{ 
                                                                                color: theme.palette.text.primary, 
                                                                                fontWeight: 600,
                                                                                flex: 1
                                                                            }}>
                                                                                {proc.name}
                                                                            </Typography>
                                                                            {!purpose?.isPublished && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleAddProcessor(proc.processorId!)}
                                                                                    disabled={addingProcessorId === proc.processorId}
                                                                                    sx={{
                                                                                        color: theme.palette.success.main,
                                                                                        bgcolor: theme.palette.error.light,
                                                                                        '&:hover': {
                                                                                            bgcolor: theme.palette.success.main,
                                                                                            color: theme.palette.success.contrastText,
                                                                                        },
                                                                                        '&:disabled': {
                                                                                            bgcolor: theme.palette.action.disabledBackground,
                                                                                            color: theme.palette.action.disabled
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {addingProcessorId === proc.processorId ? (
                                                                                        <CircularProgress size={16} color="inherit" />
                                                                                    ) : (
                                                                                        <AddIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                        
                                                                        {proc.description && (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                                {proc.description}
                                                                            </Typography>
                                                                        )}
                                                                        
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Type:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{proc.type}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Website:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{proc.website || 'N/A'}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Email:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{proc.contactEmail || 'N/A'}</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>
                        )}
                        {currentTab === 4 && (
                            <Box sx={{ p: 3 }}>
                                {loadingDataElements ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Grid container spacing={3}>
                                        {/* Mapped Data Elements */}
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <CardContent sx={{ flex: 1, p: 0 }}>
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: theme.palette.background.default
                                                    }}>
                                                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                                            Mapped Data Elements ({mappedDataElements.length})
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 500 }}>
                                                        {mappedDataElements.length === 0 ? (
                                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    No data elements mapped to this purpose yet.
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            mappedDataElements.map((mapping, index) => (
                                                                <Box key={mapping.mappingId}>
                                                                    <Box sx={{ 
                                                                        p: 2, 
                                                                        borderBottom: index < mappedDataElements.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                                        '&:hover': {
                                                                            bgcolor: theme.palette.action.hover
                                                                        }
                                                                    }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                            <Typography variant="subtitle1" sx={{ 
                                                                                color: theme.palette.text.primary, 
                                                                                fontWeight: 600,
                                                                                flex: 1
                                                                            }}>
                                                                                {mapping.element.name}
                                                                            </Typography>
                                                                            {!purpose?.isPublished && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleRemoveDataElement(mapping.elementId!)}
                                                                                    disabled={removingDataElementId === mapping.elementId}
                                                                                    sx={{
                                                                                        color: theme.palette.error.main,
                                                                                        bgcolor: theme.palette.error.light,
                                                                                        '&:hover': {
                                                                                            bgcolor: theme.palette.error.main,
                                                                                            color: theme.palette.error.contrastText,
                                                                                        },
                                                                                        '&:disabled': {
                                                                                            bgcolor: theme.palette.action.disabledBackground,
                                                                                            color: theme.palette.action.disabled
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {removingDataElementId === mapping.elementId ? (
                                                                                        <CircularProgress size={16} color="inherit" />
                                                                                    ) : (
                                                                                        <RemoveIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                        
                                                                        {mapping.element.description && (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                                {mapping.element.description}
                                                                            </Typography>
                                                                        )}
                                                                        
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Code:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{mapping.element.elementCode}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Type:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{mapping.element.type || 'N/A'}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Personal Data:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                    {mapping.element.isPersonalData ? 'Yes' : 'No'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Available Data Elements */}
                                        <Grid item xs={12} md={6}>
                                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                                <CardContent sx={{ flex: 1, p: 0 }}>
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        bgcolor: theme.palette.background.default
                                                    }}>
                                                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                                                            Available Data Elements ({filteredAvailableDataElements.length})
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Search data elements..."
                                                            value={dataElementsSearchTerm}
                                                            onChange={(e) => setDataElementsSearchTerm(e.target.value)}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Box>

                                                    <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 400 }}>
                                                        {filteredAvailableDataElements.length === 0 ? (
                                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                    {dataElementsSearchTerm ? 'No data elements match your search.' : 'No available data elements to add.'}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            filteredAvailableDataElements.map((element, index) => (
                                                                <Box key={element.elementId}>
                                                                    <Box sx={{ 
                                                                        p: 2, 
                                                                        borderBottom: index < filteredAvailableDataElements.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                                        '&:hover': {
                                                                            bgcolor: theme.palette.action.hover
                                                                        }
                                                                    }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                            <Typography variant="subtitle1" sx={{ 
                                                                                color: theme.palette.text.primary, 
                                                                                fontWeight: 600,
                                                                                flex: 1
                                                                            }}>
                                                                                {element.elementName}
                                                                            </Typography>
                                                                            {!purpose?.isPublished && (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => handleAddDataElement(element.elementId!)}
                                                                                    disabled={addingDataElementId === element.elementId}
                                                                                    sx={{
                                                                                        color: theme.palette.success.main,
                                                                                        bgcolor: theme.palette.success.light,
                                                                                        '&:hover': {
                                                                                            bgcolor: theme.palette.success.main,
                                                                                            color: theme.palette.success.contrastText,
                                                                                        },
                                                                                        '&:disabled': {
                                                                                            bgcolor: theme.palette.action.disabledBackground,
                                                                                            color: theme.palette.action.disabled
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {addingDataElementId === element.elementId ? (
                                                                                        <CircularProgress size={16} color="inherit" />
                                                                                    ) : (
                                                                                        <AddIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                        
                                                                        {element.description && (
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                                {element.description}
                                                                            </Typography>
                                                                        )}
                                                                        
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Code:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{element.elementCode}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Type:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>{element.type?.typeName || 'N/A'}</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                <Typography variant="body2" color="text.secondary">Personal Data:</Typography>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                    {element.isPersonalData ? 'Yes' : 'No'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                )}
                            </Box>
                        )}
                    </Box>
                    
                    {/* Footer for Details tab */}
                    {currentTab === 0 && !purpose?.isPublished && (
                        <Box sx={{
                            p: 2,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            bgcolor: theme.palette.background.paper
                        }}>
                            <Button
                                onClick={handleClose}
                                disabled={saving}
                                sx={{ color: theme.palette.text.primary }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSaveDetails}
                                disabled={saving || !hasDetailsChanged()}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        bgcolor: theme.palette.primary.dark,
                                    },
                                    minWidth: 100,
                                    position: 'relative'
                                }}
                            >
                                {saving ? (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            color: theme.palette.primary.contrastText,
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            marginTop: '-12px',
                                            marginLeft: '-12px',
                                        }}
                                    />
                                ) : (
                                    'Save'
                                )}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Modal>
            
            <Snackbar 
                open={saveSuccess} 
                autoHideDuration={3000}
                onClose={() => setSaveSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    {currentTab === 2 ? 'Preferences updated successfully' : 
                     currentTab === 3 ? 'Processors updated successfully' : 
                     currentTab === 4 ? 'Data Elements updated successfully' :
                     'Purpose updated successfully'}
                </Alert>
            </Snackbar>
            
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditPurposePanel; 